import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Grid, Typography, CircularProgress, Alert, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

import { JalaliDatePickerField } from "../Components/JalaliDateField";
const COLORS = ["#4c6ef5", "#22c55e", "#fbbc04", "#ec4899", "#06b6d4"];

const toSafeAmount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
};

const toSafeCurrencyLabel = (value) => {
  const label = String(value || "").trim();
  return label || "Unknown";
};

const buildBarSeries = (source = []) =>
  Object.fromEntries(
    (source || []).map((item) => [
      toSafeCurrencyLabel(item?.currency),
      toSafeAmount(item?.amount),
    ])
  );

const Dashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const themeDirection = localStorage.getItem("direction");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState("");
  const formattedStart = startDate?.format("YYYY-MM-DD") || "";
  const formattedEnd = endDate?.format("YYYY-MM-DD") || "";
  const [dateError, setDateError] = useState(null);
  const { data: branchesData } = useFetchObjects(
    ["dashboard-branches"],
    "shop/shop/?page_size=1000",
    token
  );
  const branches = Array.isArray(branchesData)
    ? branchesData
    : branchesData?.results || [];
  const dashboardQuery = `shop/financial-report/?start_date=${formattedStart}&end_date=${formattedEnd}&shop=${selectedBranch}`;

  useEffect(() => {
    if (startDate && endDate && endDate.isBefore(startDate)) {
      setDateError("Invalid date range: End date must be after start date.");
    } else {
      setDateError(null);
    }
  }, [startDate, endDate]);

  const { data, isLoading, isError, error } = useFetchObjects(
    ["dashboardReport", formattedStart, formattedEnd, selectedBranch],
    dashboardQuery,
    token
  );

  const pieDataFrom = (type) => {
    if (!data || !data[type] || !Array.isArray(data[type]) || data[type].length === 0) {
      return [];
    }
    return data[type]
      .map((item) => {
        const val = toSafeAmount(item?.amount);
        return {
          id: toSafeCurrencyLabel(item?.currency),
          label: toSafeCurrencyLabel(item?.currency),
          value: val,
        };
      })
      .filter((item) => Number.isFinite(item.value) && item.value > 0);
  };

  const barData = useMemo(() => {
    if (!data?.total_sales || !data?.total_purchases || !Array.isArray(data.total_sales) || !Array.isArray(data.total_purchases)) {
      return [];
    }
    const purchasesMap = buildBarSeries(data.total_purchases);
    const salesMap = buildBarSeries(data.total_sales);
    const allCurrencies = [...new Set([...Object.keys(purchasesMap), ...Object.keys(salesMap)])];
    
    if (allCurrencies.length === 0) return [];
    
    const result = allCurrencies.map((currency) => ({
      currency: toSafeCurrencyLabel(currency),
      sales: Number(salesMap[currency]) || 0,
      purchases: Number(purchasesMap[currency]) || 0,
    })).filter((item) =>
      item.currency &&
      Number.isFinite(item.sales) &&
      Number.isFinite(item.purchases)
    );

    return result.some(item => item.sales > 0 || item.purchases > 0) ? result : [];
  }, [data]);

  const paymentData = useMemo(() => {
    if (!data?.total_sales_paid || !data?.total_purchases_paid || !Array.isArray(data.total_sales_paid) || !Array.isArray(data.total_purchases_paid)) {
      return [];
    }
    const purchasesPaidMap = buildBarSeries(data.total_purchases_paid);
    const salesPaidMap = buildBarSeries(data.total_sales_paid);
    const allCurrencies = [...new Set([...Object.keys(purchasesPaidMap), ...Object.keys(salesPaidMap)])];
    
    if (allCurrencies.length === 0) return [];
    
    const result = allCurrencies.map((currency) => ({
      currency: toSafeCurrencyLabel(currency),
      sales_paid: Number(salesPaidMap[currency]) || 0,
      purchases_paid: Number(purchasesPaidMap[currency]) || 0,
    })).filter((item) =>
      item.currency &&
      Number.isFinite(item.sales_paid) &&
      Number.isFinite(item.purchases_paid)
    );

    return result.some(item => item.sales_paid > 0 || item.purchases_paid > 0) ? result : [];
  }, [data]);

  const renderSummaryCard = (title, items = [], subtitle = null, isCalculated = false, incomes = [], outcomes = [], expenses = [], payrolls = []) => {
    let displayItems = Array.isArray(items) ? items : [];
    
    // If this is a calculated field, calculate it properly
    if (isCalculated) {
      const incomeMap = {};
      const outcomeMap = {};
      const expenseMap = {};
      const payrollMap = {};
      
      (incomes || []).forEach(item => {
        const currency = item.currency || '---';
        incomeMap[currency] = (incomeMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      (outcomes || []).forEach(item => {
        const currency = item.currency || '---';
        outcomeMap[currency] = (outcomeMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      (expenses || []).forEach(item => {
        const currency = item.currency || '---';
        expenseMap[currency] = (expenseMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      (payrolls || []).forEach(item => {
        const currency = item.currency || '---';
        payrollMap[currency] = (payrollMap[currency] || 0) + (Number(item.amount) || 0);
      });
      
      const allCurrencies = [...new Set([
        ...Object.keys(incomeMap), 
        ...Object.keys(outcomeMap),
        ...Object.keys(expenseMap),
        ...Object.keys(payrollMap)
      ])];
      
      displayItems = allCurrencies.map(currency => ({
        currency,
        amount: (incomeMap[currency] || 0) + (outcomeMap[currency] || 0) - (expenseMap[currency] || 0) - (payrollMap[currency] || 0)
      }));
    }
    
    return (
      <Card
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 3,
          background: "#fff",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-2px)",
          },
          direction: themeDirection,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }} color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: "#4c6ef5",
              borderRadius: "50%",
              p: 0.7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AttachMoneyIcon fontSize="small" sx={{ color: "#fff" }} />
          </Box>
        </Box>
        <Box sx={{ mt: 1 }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : displayItems && displayItems.length > 0 ? (
            displayItems.map((item, idx) => {
              const rawAmount = Number(item.amount);
              const amount = isNaN(rawAmount) ? 0 : rawAmount;
              const isNegative = amount < 0;
              return (
                <Typography 
                  key={idx} 
                  variant="h5" 
                  fontWeight={700}
                  sx={{ 
                    color: isCalculated ? (isNegative ? 'error.main' : 'success.main') : 'text.primary',
                    mb: idx < displayItems.length - 1 ? 1 : 0
                  }}
                >
                  {isCalculated && !isNegative ? '+' : ''}{amount.toFixed(2)} {String(item.currency || "---")}
                </Typography>
              );
            })
          ) : (
            <Typography variant="h5" fontWeight={700} color="text.secondary">
              0.00 ---
            </Typography>
          )}
        </Box>
      </Card>
    );
  };

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Error loading dashboard: {error?.message || "Unknown error"}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 4, mt: 4, direction: themeDirection }}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h4">{t("dashboard.dashboard")}</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="dashboard-branch-label">Branch</InputLabel>
              <Select
                labelId="dashboard-branch-label"
                value={selectedBranch}
                label="Branch"
                onChange={(e) => setSelectedBranch(e.target.value)}
              >
                <MenuItem value="">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={String(branch.id)}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <JalaliDatePickerField
                label="Start date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    inputProps: { style: { direction: "ltr" } },
                  },
                }}
                sx={{ direction: "ltr" }}
              />
              <JalaliDatePickerField
                label="End date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    inputProps: { style: { direction: "ltr" } },
                  },
                }}
                sx={{ direction: "ltr" }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                setSelectedBranch("");
              }}
              sx={{ width: "100px" }}
            >
              {t("dashboard.clearBtn")}
            </Button>
          </Box>
          {dateError && <Typography sx={{ color: "darkred" }}>{dateError}</Typography>}
        </Grid>
      </Grid>

      {/* Main Financial Overview */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.totalPurchase"), data?.total_purchases, t("dashboard.totalPurchaseInvoiceAmount"))}
        {renderSummaryCard(t("dashboard.totalInvoiceAmount"), data?.total_sales, t("dashboard.totalSalesInvoiceAmount"))}
        {renderSummaryCard(t("dashboard.totalIncomes"), data?.total_incomes, t("dashboard.totalIncomeReceived"))}
      </Box>

      {/* Purchase Details */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.TotalPurchasePaid"), data?.total_purchases_paid, t("dashboard.amountPaidToSuppliers"))}
        {renderSummaryCard(t("dashboard.TotalPurchaseUnpaid"), data?.total_purchases_unpaid, t("dashboard.remainingBalanceToPay"))}
        {renderSummaryCard(t("dashboard.TotalPurchaseReturn"), data?.total_purchase_returns, t("dashboard.purchaseReturns"))}
      </Box>

      {/* Sales Details */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.totalCollected"), data?.total_collected, t("dashboard.amountReceivedFromCustomers"))}
        {renderSummaryCard(t("dashboard.TotalSalesUnpaid"), data?.total_sales_unpaid, t("dashboard.remainingBalanceToReceive"))}
        {renderSummaryCard(t("dashboard.TotalReturn"), data?.total_returns, t("dashboard.salesReturns"))}
      </Box>

      {/* Expenses & Other */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        {renderSummaryCard(t("dashboard.payrolls"), data?.total_payrolls, t("dashboard.totalPayrollExpenses"))}
        {renderSummaryCard(t("dashboard.expenses"), data?.total_expenses, t("dashboard.totalBusinessExpenses"))}
        {renderSummaryCard(t("dashboard.advance"), data?.total_advance, t("dashboard.advancePayments"))}
        {renderSummaryCard(t("dashboard.customerOpeningBalance"), data?.customer_opening_balance, t("dashboard.existingDuesFromCustomers"))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(1, 1fr)", lg: "repeat(4, 1fr)" }, gap: 3 }}>
        {[
          { title: t("dashboard.PurchaseByCurrency"), data: pieDataFrom("total_purchases") },
          { title: t("dashboard.SalesByCurrency"), data: pieDataFrom("total_sales") },
          { title: t("dashboard.SaleReturnByCurrency"), data: pieDataFrom("total_returns") },
          { title: t("dashboard.PurchaseReturnByCurrency"), data: pieDataFrom("total_purchase_returns") },
        ].map(({ title, data: chartData }) => (
          <Card key={title} elevation={3} sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              {title}
            </Typography>
            <Box sx={{ height: 260 }}>
              {chartData && chartData.length > 0 ? (
                <ResponsivePie
                  data={chartData}
                  margin={{ top: 10, right: 20, bottom: 40, left: 20 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  colors={COLORS}
                  activeOuterRadiusOffset={8}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  legends={[{ anchor: "bottom", direction: "row", translateY: 32, itemWidth: 80, itemHeight: 14 }]}
                />
              ) : (
                <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">{t("dashboard.noData")}</Typography>
                </Box>
              )}
            </Box>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: 3 }}>
        <Card elevation={3} sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t("dashboard.SalesVsPurchases")}
          </Typography>
          <Box sx={{ height: 260 }}>
            {barData && barData.length > 0 ? (
              <ResponsiveBar
                data={barData}
                keys={["sales", "purchases"]}
                indexBy="currency"
                margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
                padding={0.3}
                axisTop={null}
                axisRight={null}
                axisBottom={{ tickSize: 0, tickPadding: 12 }}
                axisLeft={{ tickSize: 0, tickPadding: 12 }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 32,
                    itemWidth: 90,
                    itemHeight: 18,
                    itemsSpacing: 8,
                    symbolSize: 12,
                    symbolShape: "circle",
                  },
                ]}
                colors={{ scheme: "nivo" }}
              />
            ) : (
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t("dashboard.noData")}</Typography>
              </Box>
            )}
          </Box>
        </Card>

        <Card elevation={3} sx={{ p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t("dashboard.salesPaymentVsPurchasePayment")}
          </Typography>
          <Box sx={{ height: 260 }}>
            {paymentData && paymentData.length > 0 ? (
              <ResponsiveBar
                data={paymentData}
                keys={["sales_paid", "purchases_paid"]}
                indexBy="currency"
                margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
                padding={0.3}
                axisTop={null}
                axisRight={null}
                axisBottom={{ tickSize: 0, tickPadding: 12 }}
                axisLeft={{ tickSize: 0, tickPadding: 12 }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 32,
                    itemWidth: 120,
                    itemHeight: 18,
                    itemsSpacing: 8,
                    symbolSize: 12,
                    symbolShape: "circle",
                  },
                ]}
                colors={["#4CAF50", "#FF9800"]}
              />
            ) : (
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t("dashboard.noData")}</Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
